"use client";

import { useCallback, useEffect, useSyncExternalStore } from "react";
import type { ConsentState, ConsentAction, CookieCategory } from "./cookie-consent.types";
import { CONSENT_EVENT } from "./cookie-consent.types";
import {
  getConsentCookie,
  setConsentCookie,
  clearConsentCookie,
  dispatchConsentEvent,
  sendConsentLog,
  ALL_ACCEPTED,
  ONLY_NECESSARY,
} from "./cookie-consent.utils";

let snapshotCache: ConsentState | null = getConsentCookie();

const subscribe = (callback: () => void): (() => void) => {
  const handler = () => {
    snapshotCache = getConsentCookie();
    callback();
  };
  window.addEventListener(CONSENT_EVENT, handler);
  return () => window.removeEventListener(CONSENT_EVENT, handler);
};

const getSnapshot = (): ConsentState | null => snapshotCache;
const getServerSnapshot = (): ConsentState | null => null;

interface UseCookieConsentOptions {
  apiUrl?: string;
  sourceApp?: string;
}

export const useCookieConsent = (options: UseCookieConsentOptions = {}) => {
  const { apiUrl, sourceApp = "landing" } = options;
  const consent = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

  useEffect(() => {
    snapshotCache = getConsentCookie();
  }, []);

  const save = useCallback(
    (state: ConsentState, action: ConsentAction) => {
      const stamped = { ...state, timestamp: new Date().toISOString() };
      setConsentCookie(stamped);
      snapshotCache = stamped;
      dispatchConsentEvent(stamped);
      if (apiUrl) {
        sendConsentLog(apiUrl, stamped, action, sourceApp);
      }
    },
    [apiUrl, sourceApp],
  );

  const acceptAll = useCallback(() => save(ALL_ACCEPTED, "accept_all"), [save]);

  const rejectNonEssential = useCallback(
    () => save(ONLY_NECESSARY, "reject_non_essential"),
    [save],
  );

  const saveCustom = useCallback(
    (categories: Pick<ConsentState, CookieCategory>) =>
      save({ ...categories, necessary: true, timestamp: "", version: "" }, "customize"),
    [save],
  );

  const hasConsented = consent !== null;
  const hasCategory = useCallback(
    (category: CookieCategory): boolean => {
      if (!consent) return category === "necessary";
      return consent[category] === true;
    },
    [consent],
  );

  const resetConsent = useCallback(() => {
    clearConsentCookie();
    snapshotCache = null;
    dispatchConsentEvent({ necessary: true, analytics: false, functional: false, marketing: false, timestamp: "", version: "" });
  }, []);

  return {
    consent,
    hasConsented,
    hasCategory,
    acceptAll,
    rejectNonEssential,
    saveCustom,
    resetConsent,
  };
};
